import React, { useEffect, useState, useCallback } from 'react';
import { getEmployees, getActivity, postActivity, getTimeEntries } from '../api';
import { formatDateTime } from '../utils';
import { useAuth } from '../AuthContext';

const EVENT_TYPES = ['login', 'logout', 'app_switch', 'idle', 'active', 'meeting', 'break', 'note'];
const EVENT_COLORS = { login: 'badge-green', logout: 'badge-red', app_switch: 'badge-blue', idle: 'badge-orange', active: 'badge-green', meeting: 'badge-blue', break: 'badge-orange', note: 'badge-gray' };

export default function Activity() {
  const { user, isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState('');
  const [events, setEvents] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ event_type: 'note', description: '' });

  const load = useCallback(async () => {
    if (!selectedEmp) return;
    const from = new Date(Date.now() - 7 * 86400000).toISOString();
    const data = await getActivity({ employee_id: selectedEmp, from });
    setEvents(data);
  }, [selectedEmp]);

  useEffect(() => {
    if (!isAdmin && user?.employee_id) {
      // Employees can only see their own activity
      setSelectedEmp(user.employee_id);
      return;
    }
    getEmployees().then(emps => {
      setEmployees(emps);
      if (emps.length) setSelectedEmp(emps[0].id);
    });
  }, [isAdmin, user?.employee_id]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    await postActivity({ employee_id: selectedEmp, ...form });
    setShowAdd(false);
    setForm({ event_type: 'note', description: '' });
    load();
  };

  // Group events by date
  const grouped = events.reduce((acc, ev) => {
    const date = ev.occurred_at.split('T')[0];
    if (!acc[date]) acc[date] = [];
    acc[date].push(ev);
    return acc;
  }, {});

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Activity Timeline</h1>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Log Event</button>
      </div>

      {isAdmin && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Employee</label>
            <select value={selectedEmp} onChange={e => setSelectedEmp(e.target.value)}>
              {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
        </div>
      )}

      {Object.keys(grouped).length === 0 ? (
        <div className="card empty">No activity events in the last 7 days.</div>
      ) : (
        Object.keys(grouped).sort().reverse().map(date => (
          <div key={date} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.5px' }}>
              {new Date(date).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
            <div style={{ position: 'relative', paddingLeft: 24 }}>
              <div style={{ position: 'absolute', left: 8, top: 0, bottom: 0, width: 2, background: 'var(--border)' }} />
              {grouped[date].map((ev, i) => (
                <div key={ev.id} style={{ position: 'relative', marginBottom: 12 }}>
                  <div style={{ position: 'absolute', left: -20, top: 4, width: 10, height: 10, borderRadius: '50%', background: 'var(--primary)', border: '2px solid var(--card)' }} />
                  <div className="card" style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className={`badge ${EVENT_COLORS[ev.event_type] || 'badge-gray'}`}>{ev.event_type}</span>
                      <span className="text-muted" style={{ fontSize: '0.8rem' }}>{formatDateTime(ev.occurred_at)}</span>
                    </div>
                    {ev.description && <div style={{ marginTop: 6, fontSize: '0.875rem' }}>{ev.description}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Log Activity Event</div>
            <form onSubmit={handleAdd}>
              {isAdmin && (
                <div className="form-group">
                  <label>Employee</label>
                  <select value={selectedEmp} onChange={e => setSelectedEmp(e.target.value)}>
                    {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Event Type</label>
                <select value={form.event_type} onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))}>
                  {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional details" />
              </div>
              <div className="flex gap-8" style={{ justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-outline" onClick={() => setShowAdd(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Log Event</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
